import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { useMobile } from './hooks/useMobile';
import { isPWA } from './utils/capacitor';
import PWAInstallBanner from './components/PWAInstallBanner';
import LoadingScreen from './components/LoadingScreen';
import { AppLoadingSpinner } from './components/AppLoadingSpinner';
import { SessionInvalidatedScreen } from './components/SessionInvalidatedScreen';
import { LogoutHandler } from './components/LogoutHandler';
import { PendingDeletionGuard } from './components/PendingDeletionGuard';
import { apiClient, isStoredAuthTokenJwt, looksLikeJwt } from './api/client';
import { API_ENDPOINTS } from './api/endpoints';
import { isCognitoConfigured } from './config/cognito';
import { cognitoAuthService } from './services/cognitoAuth.service';
import { isTokenExpiringSoon } from './utils/jwtExpiry';
import { restartMarqueeAnimations } from './utils/marqueeRestart';

// Lazy-load route chunks to reduce initial bundle and speed first paint; tier pages load on demand
const LandingPage = lazy(() => import('./pages/LandingPage'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const AcceptTerms = lazy(() => import('./pages/AcceptTerms'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TextMessagingConsent = lazy(() => import('./pages/TextMessagingConsent'));
const FreeTrial = lazy(() => import('./pages/FreeTrial'));
const Starter = lazy(() => import('./pages/Starter'));
const Professional = lazy(() => import('./pages/Professional'));
const AIPro = lazy(() => import('./pages/AIPro'));
const SubscriptionRequired = lazy(() => import('./pages/SubscriptionRequired'));
const ReactivatePage = lazy(() => import('./pages/ReactivatePage'));
const ChoosePlan = lazy(() => import('./pages/ChoosePlan'));
const PlanDetail = lazy(() => import('./pages/PlanDetail'));

// Prefetch tier pages after idle so navigation feels instant (user lands on / then goes to /pricing/starter etc.)
const prefetchTierRoutes = () => {
  import('./pages/Starter');
  import('./pages/FreeTrial');
  import('./pages/ChoosePlan');
};

/** Hides page content during route transition so no bottom-of-page glitch; shows content only after loading screen finishes. */
function TransitionWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = React.useState(true);
  const [quickbooksPopupSuccess, setQuickbooksPopupSuccess] = React.useState(false);

  React.useEffect(() => {
    setIsTransitioning(true);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!isTransitioning) restartMarqueeAnimations();
  }, [isTransitioning]);

  // When QuickBooks OAuth completed in this tab (opened as popup), tell the opener
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('quickbooks') === 'connected' && typeof window !== 'undefined' && window.opener) {
      window.opener.postMessage({ type: 'quickbooks-connected' }, window.location.origin);
      setQuickbooksPopupSuccess(true);
    }
  }, [location.search]);

  // Memoize callback to prevent LoadingScreen useEffect from re-running
  const handleTransitionComplete = React.useCallback(() => {
    setIsTransitioning(false);
  }, []);

  return (
    <>
      <LoadingScreen
        key={location.pathname || '/'}
        onTransitionComplete={handleTransitionComplete}
      />
      {quickbooksPopupSuccess && (
        <div
          className="fixed top-0 left-0 right-0 z-[10000] bg-emerald-600 text-white text-center py-2 px-4 text-sm font-medium shadow"
          role="status"
        >
          QuickBooks connected. You can close this tab and return to your other Spinora tab.
        </div>
      )}
      <div
        style={{
          visibility: isTransitioning ? 'hidden' : 'visible',
          pointerEvents: isTransitioning ? 'none' : 'auto',
          opacity: 1,
        }}
      >
        {children}
      </div>
    </>
  );
}



const SESSION_CHECK_THROTTLE_MS = 5000;   // Don't re-check within 5s on focus/visibility
const SESSION_CHECK_INTERVAL_MS = 15000;  // Poll every 15s for other-device login (single session); less aggressive to avoid false invalidation
const TOKEN_REFRESH_CHECK_MS = 60 * 1000;   // Check every 60s when active — never hit Cognito's 1hr expiry
const TOKEN_EXPIRY_BUFFER_MS = 20 * 60 * 1000; // Refresh when < 20 min left (industry: proactive refresh well before expiry)

function App() {
  const isMobile = useMobile();
  const isPWAInstalled = isPWA();
  const lastSessionCheckRef = useRef<number>(0);

  // Refresh Cognito token proactively so we never hit expiry during active use (no hourly sign-out).
  useEffect(() => {
    if (!isCognitoConfigured() || !isStoredAuthTokenJwt()) return;
    const refreshToken = () => {
      if (!isStoredAuthTokenJwt()) return;
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
      // Refresh immediately if token expires within 5 min (avoids mid-invoice 401)
      if (token && isTokenExpiringSoon(token, TOKEN_EXPIRY_BUFFER_MS)) {
        cognitoAuthService.getCurrentSession()
          .then((session) => {
            const newToken = session.getIdToken().getJwtToken();
            if (newToken && typeof localStorage !== 'undefined') localStorage.setItem('authToken', newToken);
          })
          .catch(() => {});
        return;
      }
      // Otherwise refresh on interval to stay ahead of expiry
      cognitoAuthService.getCurrentSession()
        .then((session) => {
          const newToken = session.getIdToken().getJwtToken();
          if (newToken && typeof localStorage !== 'undefined') localStorage.setItem('authToken', newToken);
        })
        .catch(() => {});
    };
    const intervalId = window.setInterval(refreshToken, TOKEN_REFRESH_CHECK_MS);
    refreshToken();
    return () => window.clearInterval(intervalId);
  }, []);

  // Single-session: only one session per account. When user logs in elsewhere, this session is invalidated.
  // Poll /auth/me to detect "signed in on another device" (backend rejects tokens from before lastLoginAt).
  useEffect(() => {
    const checkSession = () => {
      if (!isStoredAuthTokenJwt()) return;
      const now = Date.now();
      if (now - lastSessionCheckRef.current < SESSION_CHECK_THROTTLE_MS) return;
      lastSessionCheckRef.current = now;
      apiClient.get(API_ENDPOINTS.AUTH.ME, { silent401: true }).catch(() => {});
    };

    const onVisibilityOrFocus = () => {
      if (!isStoredAuthTokenJwt()) return;
      (async () => {
        if (isCognitoConfigured()) {
          try {
            const session = await cognitoAuthService.getCurrentSession();
            const token = session.getIdToken().getJwtToken();
            if (token) localStorage.setItem('authToken', token);
          } catch { /* ignore */ }
        }
        checkSession();
      })();
    };

    const timeoutId = window.setTimeout(checkSession, 800);
    const intervalId = window.setInterval(checkSession, SESSION_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
    };
  }, []);

  // Multi-tab: when authToken is cleared in another tab, show session invalidated in this tab too.
  // Pass through the reason (e.g. SESSION_INVALIDATED) so we show the same message as the clearing tab.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'authToken' && e.newValue === null && e.storageArea === localStorage && looksLikeJwt(e.oldValue)) {
        const reason = typeof localStorage !== 'undefined' ? localStorage.getItem('spinora-session-invalidated-reason') : null;
        if (reason) localStorage.removeItem('spinora-session-invalidated-reason');
        const detail = reason === 'SESSION_INVALIDATED' ? { code: 'SESSION_INVALIDATED' as const } : {};
        window.dispatchEvent(new CustomEvent('spinora-session-invalidated', { detail }));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Marquee banners: pause when tab is hidden; on resume / bfcache / route reveal, restart CSS animation (avoids stuck scroll).
  useEffect(() => {
    const onVisibility = () => {
      const hidden = document.hidden;
      document.body.classList.toggle('marquee-paused', hidden);
      if (!hidden) restartMarqueeAnimations();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      document.body.classList.remove('marquee-paused');
      if (e.persisted || document.visibilityState === 'visible') restartMarqueeAnimations();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    onVisibility();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  // Prefetch tier pages when user has token (likely to navigate after login) — improves perceived speed
  useEffect(() => {
    if (!isStoredAuthTokenJwt()) return;
    const t = setTimeout(() => {
      import('./pages/Starter');
      import('./pages/Professional');
      import('./pages/AIPro');
      import('./pages/FreeTrial');
    }, 400);
    return () => clearTimeout(t);
  }, []);

  // Prefetch tier routes after initial load so post-login navigation feels instant
  useEffect(() => {
    const t = window.setTimeout(prefetchTierRoutes, 300);
    return () => window.clearTimeout(t);
  }, []);

  // 402 subscription inactive: redirect to billing resolution (no URL bypass; server already blocked access).
  useEffect(() => {
    const onSubscriptionInactive = () => {
      const path = window.location.pathname || '';
      if (path !== '/subscription-required') {
        window.location.href = '/subscription-required';
      }
    };
    window.addEventListener('spinora-subscription-inactive', onSubscriptionInactive);
    return () => window.removeEventListener('spinora-subscription-inactive', onSubscriptionInactive);
  }, []);

  return (
    <ThemeProvider>
      {isPWAInstalled && (
        <div className="status-bar-area" style={{
          height: 'env(safe-area-inset-top)',
          backgroundColor: '#000000',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          width: '100%'
        }} />
      )}
      <div
        className="app-container"
        style={{
          width: '100%',
          minHeight: '-webkit-fill-available',
          overflowX: 'hidden',
          position: 'relative',
          WebkitOverflowScrolling: 'touch',
          paddingTop: isPWAInstalled ? 'env(safe-area-inset-top)' : '0',
          paddingBottom: isPWAInstalled ? 'env(safe-area-inset-bottom)' : (isMobile && !isPWAInstalled ? '72px' : '0'),
          paddingLeft: isPWAInstalled ? 'env(safe-area-inset-left)' : '0',
          paddingRight: isPWAInstalled ? 'env(safe-area-inset-right)' : '0'
        }}
      >
        <Router>
            <SessionInvalidatedScreen />
            <LogoutHandler />
            <TransitionWrapper>
              <PendingDeletionGuard>
              <Suspense
                fallback={
                  <div className="fixed inset-0 bg-black z-[99998] flex items-center justify-center" style={{ transform: 'translateZ(0)' }}>
                    <AppLoadingSpinner />
                  </div>
                }
              >
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/accept-terms" element={<AcceptTerms />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/text-messaging-consent" element={<TextMessagingConsent />} />
                  <Route path="/pricing/free-trial" element={<FreeTrial />} />
                  <Route path="/pricing/starter" element={<Starter />} />
                  <Route path="/pricing/professional" element={<Professional />} />
                  <Route path="/pricing/ai-pro" element={<AIPro />} />
                  <Route path="/subscription-required" element={<SubscriptionRequired />} />
                  <Route path="/reactivate" element={<ReactivatePage />} />
                  <Route path="/choose-plan" element={<ChoosePlan />} />
                  <Route path="/plan/:slug" element={<PlanDetail />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
              </PendingDeletionGuard>
            </TransitionWrapper>
        </Router>
        <PWAInstallBanner />
      </div>
    </ThemeProvider>
  );
}

export default App; 